// =============================================================================
// features/sales/agenda/components/LeadPickerDialog.tsx — Seletor de lead da
// Agenda comercial (F35-S24, doc 26 §6.1).
//
// A Agenda começa do zero (ao contrário do live chat/CRM, que já estão
// "dentro" de um lead) — este diálogo resolve esse `leadId` antes de abrir o
// `ScheduleAppointmentDialog` REUTILIZADO (features/sales/components/, fora
// de escopo deste slot).
//
// Escopo/RBAC: a busca (`useLeadSearch`, hooks/sales/) chama a MESMA rota
// GET /api/leads do CRM. O servidor já filtra por `organization_id` +
// `city_scope` (apps/api/.../leads/service.ts) — um lead fora do escopo do
// ator nunca aparece na lista, nunca "aparece e falha ao salvar". Não há
// filtro client-side aqui: filtrar no cliente exigiria que o dado já tivesse
// chegado ao navegador, e esse seria o vazamento (nota do slot).
//
// Estados: idle (< 2 chars), carregando, sem permissão (403 → NUNCA "sem
// resultado"), erro de busca (com retry), vazio, resultados.
//
// DS: painel `--elev-5`, overlay + foco preso (useFocusTrap, compartilhado
// com ScheduleAppointmentDialog/AppointmentDrawer — nenhuma reimplementação).
// =============================================================================

import * as React from 'react';

import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { maskPhone, STATUS_META } from '../../../../hooks/crm/types';
import type { LeadResponse } from '../../../../hooks/sales/useLeadSearch';
import { useLeadSearch } from '../../../../hooks/sales/useLeadSearch';
import { cn } from '../../../../lib/cn';
import { useFocusTrap } from '../../components/useFocusTrap';

export interface LeadPickerDialogProps {
  onClose: () => void;
  onSelect: (lead: LeadResponse) => void;
}

type PickerVisualState = 'idle' | 'loading' | 'forbidden' | 'error' | 'empty' | 'results';

/**
 * Deriva o estado visual a partir do resultado de `useLeadSearch` — pura e
 * exportada para teste (sem RTL neste pacote, mesmo padrão de
 * `AppointmentDrawer.shouldBlockEscape`).
 */
export function describePickerState(search: {
  isIdle: boolean;
  isFetching: boolean;
  isForbidden: boolean;
  isError: boolean;
  resultCount: number;
}): PickerVisualState {
  if (search.isForbidden) return 'forbidden';
  if (search.isIdle) return 'idle';
  if (search.isFetching) return 'loading';
  if (search.isError) return 'error';
  if (search.resultCount === 0) return 'empty';
  return 'results';
}

export function LeadPickerDialog({ onClose, onSelect }: LeadPickerDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [term, setTerm] = React.useState('');
  const { results, isFetching, isIdle, isForbidden, isError, refetch } = useLeadSearch(term);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: onClose });

  const visualState = describePickerState({
    isIdle,
    isFetching,
    isForbidden,
    isError,
    resultCount: results.length,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-picker-title"
        className="w-full max-w-lg rounded-md flex flex-col overflow-hidden max-h-[min(80vh,640px)]"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <h2
              id="lead-picker-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Novo agendamento
            </h2>
            <p className="font-sans text-sm text-ink-3">Busque o lead por nome ou telefone.</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar seleção de lead"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5 overflow-y-auto">
          <label htmlFor="lead-picker-search" className="sr-only">
            Buscar lead por nome ou telefone
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="w-4 h-4"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M10.5 10.5 L14 14" />
              </svg>
            </span>
            <input
              id="lead-picker-search"
              type="text"
              autoComplete="off"
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Nome ou telefone…"
              className={cn(
                'w-full font-sans text-sm font-medium text-ink',
                'bg-surface-1 rounded-sm pl-9 pr-[14px] py-[11px]',
                'border border-border-strong',
                'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
                'transition-[border-color,box-shadow,background] duration-fast ease',
                'placeholder:text-ink-4',
                'hover:border-ink-3 hover:bg-surface-hover',
                'focus:outline-none focus:border-azul',
                'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
                'focus:bg-surface-1',
              )}
            />
            {isFetching && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
              >
                <span className="block w-3.5 h-3.5 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
              </span>
            )}
          </div>

          <LeadPickerResults
            state={visualState}
            results={results}
            onSelect={onSelect}
            onRetry={refetch}
          />
        </div>

        <div
          className="flex justify-end px-6 py-4 shrink-0"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Corpo de resultados por estado ─────────────────────────────────────────

interface LeadPickerResultsProps {
  state: PickerVisualState;
  results: LeadResponse[];
  onSelect: (lead: LeadResponse) => void;
  onRetry: () => void;
}

function LeadPickerResults({
  state,
  results,
  onSelect,
  onRetry,
}: LeadPickerResultsProps): React.JSX.Element | null {
  if (state === 'idle') {
    return (
      <p className="font-sans text-sm text-ink-3 text-center py-6">
        Digite ao menos 2 caracteres para buscar.
      </p>
    );
  }

  if (state === 'loading') {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Buscando leads">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-14 rounded-sm animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (state === 'forbidden') {
    return (
      <div
        role="alert"
        className="rounded-sm px-4 py-3 border-l-[3px]"
        style={{
          borderColor: 'var(--warning)',
          background: 'var(--warning-bg)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <p className="font-sans font-semibold text-sm text-ink">Sem permissão para buscar leads</p>
        <p className="font-sans text-xs text-ink-2 mt-0.5">
          Fale com um administrador se precisar buscar leads para agendar.
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div
        role="alert"
        className="rounded-sm px-4 py-3 border-l-[3px] flex items-center justify-between gap-3"
        style={{
          borderColor: 'var(--danger)',
          background: 'var(--danger-bg)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <div>
          <p className="font-sans font-semibold text-sm text-danger">Erro ao buscar leads</p>
          <p className="font-sans text-xs text-ink-2 mt-0.5">
            Verifique sua conexão e tente novamente.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <p className="font-sans text-sm text-ink-3 text-center py-6">
        Nenhum lead encontrado dentro do seu escopo.
      </p>
    );
  }

  return (
    <ul role="listbox" aria-label="Leads encontrados" className="flex flex-col gap-1">
      {results.map((lead) => {
        const meta = STATUS_META[lead.status];
        return (
          <li key={lead.id}>
            <button
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => onSelect(lead)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-sm cursor-pointer text-left',
                'transition-colors duration-fast ease',
                'hover:bg-surface-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
              )}
            >
              <span
                className="shrink-0 w-8 h-8 rounded-pill flex items-center justify-center font-sans font-bold text-xs text-white"
                style={{ background: 'var(--grad-rondonia)', boxShadow: 'var(--elev-1)' }}
                aria-hidden="true"
              >
                {lead.name.charAt(0).toUpperCase()}
              </span>

              <div className="min-w-0 flex-1">
                <p className="font-sans text-sm font-semibold text-ink truncate">{lead.name}</p>
                <p className="font-mono text-xs text-ink-3 truncate">
                  {maskPhone(lead.phone_e164)}
                </p>
              </div>

              {meta && <Badge variant={meta.variant}>{meta.label}</Badge>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
